import { selectOneSetting, selectTenantId } from "../../../../../util/store/selectors";
import { getStore } from "../../../../../util/store/store";
import { CERTAIN_TYPE, DELIVERY_TYPE, OP_STATUS, OPERATION_TYPE, SITE_TYPE, VESSEL_TYPE } from "../../../../../util/varibles/constants";
import { cloneObj, uuid } from "../../../../../util/varibles/global";
import { IDeliveryType, IFactory, IHarvest, ISite, IUnit, IVessel, TOperation } from "../../../../../util/varibles/interface";
import { PreferenceSetting } from "../../../../../util/varibles/userSetting";
import { selectPlan } from "../../../constants";
import { getDeliveryType, getFactory } from "../../../util";
import { getEditOp } from "../EditOperationContext";

export enum DELIVERY_UNIT {
    TONS = 't',
    FISH = 'fish'
}

export const deliveryUnits = [
    { id: DELIVERY_UNIT.TONS, name: 'tons' },
    { id: DELIVERY_UNIT.FISH, name: 'fish' },
]


interface ICreatHarvest {
    unit: IUnit;
    vessel: IVessel;
    properties: {
        operation_code: string
        fish_amount: number
        total_weight: number

        available_time_id?: string,
        contract_id?: string
        error_detail?: any
    };
}

export const createHarvest = ({ unit, vessel, properties }: ICreatHarvest, get: ReturnType<typeof getEditOp>): IHarvest => {
    const state = getStore();
    const config = selectOneSetting(PreferenceSetting.AUTO_PLAN)(state);
    const userTenantId = selectTenantId(state);
    const { factories, vesselTanks } = get();

    const { fish_amount, total_weight } = properties;
    const { site_id = "", site_name = "", factory_id = "", primary_factory = "" } = unit;
    const site = { id: site_id, name: site_name };

    const { factory, error_detail: factoryError = {} } = getFactory(factory_id, primary_factory, site, factories);

    const { is_journey_to_site = false, cleaning_types = [] } = config[vessel.type] || {};
    const { deliveryType, error_detail: deliveryTypeError = {} } = getDeliveryType(factory, vessel)

    return {
        ...properties,
        id: uuid(),
        tenant_id: userTenantId,
        is_journey_to_site,
        is_cleaning: new Set(cleaning_types).has(deliveryType?.type),
        current_process: -1,
        status: OP_STATUS.NEW,
        site_id,
        site_name,
        factory_id: factory.id || '',
        factory_name: factory.name,
        certain_mode: CERTAIN_TYPE.NONE,
        tanks: [],
        sub_operations: [{
            ...unit,
            fish_amount,
            total_weight,
            delivery_types: deliveryType ? [{
                id: deliveryType.id,
                name: deliveryType.name,
                type: deliveryType.type,
                fish_amount,
                total_weight,
                unit: DELIVERY_UNIT.TONS,
                tanks: deliveryType.type === DELIVERY_TYPE.DIRECT
                    ? vesselTanks.map(item => ({ id: item.id || '', name: item.name }))
                    : [{ id: vesselTanks[0].id || '', name: vesselTanks[0].name }]
            }] : [],
        }],
        error_detail: { ...factoryError, ...deliveryTypeError },
        sites: [site],
        total_amount: fish_amount,
        operation_type: OPERATION_TYPE.HARVEST,
    };
};

export enum DRAG_MODE {
    SWAP = 'swap',
    MERGE = 'merge'
}

export const initDeliveryType = (value: IDeliveryType) => {
    return {
        id: value.id,
        type: value.type,
        name: value.name,
        tanks: [],
        fish_amount: 0,
        total_weight: 0,
        unit: DELIVERY_UNIT.TONS
    }
}

export const getDeliveryTypes = (factory: IFactory, vessel: IVessel): { [id: string]: IDeliveryType } => {
    const { type: vesselType } = vessel || {};
    const { deliver_type = {} } = factory;
    if (vesselType === VESSEL_TYPE.PROCESS_VESSEL) {
        return Object.keys(deliver_type).reduce((rs: { [id: string]: IDeliveryType }, key) => {
            if (deliver_type[key].type === DELIVERY_TYPE.DIRECT) {
                rs[key] = deliver_type[key]
            }
            return rs;
        }, {})
    } else {
        return deliver_type
    }
}

export const getRootUnit = (data: TOperation) => {
    const { sub_operations, site_id, site_name } = data;
    const sub = cloneObj(sub_operations);
    const { sites, harvests } = selectPlan.root(getStore());

    return sub_operations.reduce((list: { [id: string]: IUnit }, item, index: number) => {
        const { id, harvest_id = '-' } = item;

        let siteId: string;
        if (!item.site_id) {
            const site = sites.find((site: ISite) => site.units.some((unit) => unit.id === id))
            if (site) {
                siteId = site.id || site_id;
                sub[index].site_id = site.id
                sub[index].site_name = site.name
            } else
                siteId = site_id
        } else
            siteId = item.site_id;
        const {
            units = [],
            type = SITE_TYPE.INTERNAL
        } = sites.find((item: any) => item.id === siteId) || {};
        const key = id + '|' + harvest_id;
        const unit = units.find(unit => unit.id === id);
        if (!unit)
            return list;
        list[key] = {
            ...unit,
            site_id,
            site_name,
            site_type: type,
            harvest_id
        }
        if (harvest_id !== '-')
            list[key] = { ...list[key], ...harvests.find((harvest: any) => harvest.harvest_id === harvest_id) };

        return list;
    }, {})
}